import * as React from "react";
import {useState, useRef} from "react";
import {IOddUploadResult, OddIqApi} from "../../api/OddIqApi";
import {throwErrorOnNullOrUndefined} from "../../utils/errorUtil";
import {TimerComponent} from "../common/Timer.component";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";

export const UploadOdd: React.FunctionComponent = () => {
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [uploadResult, setUploadResult] = useState<IOddUploadResult | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const renderResult = () => {
        if (!uploadResult) return null;

        const isSuccess = uploadResult.isSuccess;
        return (
            <div className={`upload-odd__upload-result ${isSuccess ? "upload-odd__upload-result--success" : "upload-odd__upload-result--error"}`} data-testid="odd__result">
                <div>
                    <strong>{isSuccess ? "Upload succeeded" : "Upload failed"}</strong>
                </div>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                        {uploadResult.errors.map((it: string, idx: number) => (
                            <div key={idx}>{it}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const uploadOdd = () => {
        setUploadResult(undefined);
        setIsLoading(true);
        const formData = new FormData();
        formData.append("file", throwErrorOnNullOrUndefined(selectedFile), selectedFile!.name);
        OddIqApi.uploadOdd(formData)
            .then(({data}) => {
                setUploadResult(data);
                clearFileInput();
                setIsLoading(false);
            })
            .catch((error) => {
                clearFileInput();
                setIsLoading(false);
                const errorNumber = error.errorNumber || "Unknown";
                const errorMessage = error.message || "An unexpected error occurred";
                setUploadResult({isSuccess: false, errors: [`Upload failed: response: ${errorNumber} reason: ${errorMessage}`]});
            });
    };

    const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        setSelectedFile(e.target.files[0]);
    };

    const clearFileInput = () => {
        setSelectedFile(undefined);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    return <div className="upload-odd__card">
        <h2 className="upload-odd__card-title">Load ODD Data</h2>
        <p className="upload-odd__card-description">Upload an ODD data file to refresh the ODD IQ Dataset. The file must be in the exact required <strong>.xlsx</strong> format.</p>

        <TimerComponent isRunning={isLoading}/>
        {isLoading ? <div className="upload-odd__loading-spinner-container"><LoadingSpinner/></div> : null}

        <div className="upload-odd__file-selection-area" style={{ display: selectedFile || isLoading ? 'none' : 'flex' }}>
            <button
                className="upload-odd__btn upload-odd__btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
            >
                <i className="fa-solid fa-paperclip" /> Choose File
            </button>
            <span className="upload-odd__no-file-text">No file chosen</span>
            <input
                ref={fileInputRef}
                id="file-input"
                onChange={handleFileSelection}
                type="file"
                className="upload-odd__file-input"
                accept=".xlsx"
                disabled={isLoading}
                data-testid="odd-file-input"
            />
        </div>

        <div className={`upload-odd__file-selected ${selectedFile && !isLoading ? 'upload-odd__file-selected--active' : ''}`}>
            <i className="fa-solid fa-file-excel upload-odd__file-icon" />
            <div className="upload-odd__file-details">
                <div className="upload-odd__file-name">{selectedFile?.name}</div>
                <div className="upload-odd__file-size">{selectedFile ? formatBytes(selectedFile.size) : ''}</div>
            </div>
            <button
                className="upload-odd__remove-file"
                aria-label="Remove file"
                onClick={clearFileInput}
                disabled={isLoading}
            >
                <i className="fa-solid fa-xmark" />
            </button>
        </div>

        <div className="upload-odd__button-group">
            <button
                className="upload-odd__btn upload-odd__btn-secondary"
                onClick={clearFileInput}
                disabled={isLoading || !selectedFile}
            >
                Cancel
            </button>
            <button
                className="upload-odd__btn upload-odd__btn-primary"
                onClick={uploadOdd}
                disabled={isLoading || !selectedFile}
            >
                Upload Data
            </button>
        </div>

        {renderResult()}
    </div>;
};
